// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './CategoryQuestion.module.css'
import TeamAsked from 'components/community/discussions/TeamAsked'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { brandedTitle } from 'lib/dealhunt/url'
import type { DealHuntSuggestHit } from 'lib/dealhunt/types'
import type { DiscussionCard, DiscussionThread } from 'lib/community/discussions-types'
import { answerCountLine, discussionHref, teamAskedLine } from 'lib/community/discussions'
import { DISCUSSION_LIMITS, gearFromHit, postedRedirect } from 'lib/community/discussions-writer'
import { hitNodeId } from 'lib/community/model'
import {
  useOwnerCredentialOptional,
  useOwnerSessionOptional,
  type OwnerCredential,
} from 'lib/owner-notes/auth-bridge'
import { useOnScreenOnce, useOwnerTrack } from 'lib/owner-notes/analytics'
import { useOwnTypeahead, type OwnTypeahead } from 'lib/owner-notes/own-typeahead'
import {
  EMPTY_QUESTION_DRAFT,
  QUESTION_COPY,
  QUESTION_LOGIN_DIALOG,
  SLOT_DEBOUNCE_MS,
  SLOT_QUERY_WORDS,
  clearQuestionDraft,
  countOrNull,
  postedLine,
  questionSlot,
  reachLine,
  readQuestionDraft,
  wordCount,
  writeQuestionDraft,
  type QuestionDraft,
  type QuestionSlot,
} from 'lib/owner-notes/category-question'
import {
  fetchAnsweredQuestions,
  fetchQuestionReach,
  submitQuestion,
} from 'lib/owner-notes/category-question-api'

// ── the CTA (the declared strip's line) ──────────────────────────────────────

/** The declared strip's one line under its buy-again row. */
export const QuestionCtaLine: React.VFC<{ noun: string; onOpen: () => void }> = ({
  noun,
  onOpen,
}) => (
  <p className={styles.line}>
    {QUESTION_COPY.declaredLead(noun)}
    <button type="button" className={styles.lineLink} onClick={onOpen}>
      {QUESTION_COPY.declaredLink}
    </button>
  </p>
)

// ── the composer (client-only: it exists only after a click) ─────────────────

export interface QuestionPosted {
  href: string
  line: string
  pill: string
}

export interface QuestionComposerViewProps {
  noun: string
  draft: QuestionDraft
  reach: string | null
  slot: QuestionSlot
  /** The scoped product search; null once a chip is linked. */
  typeahead: OwnTypeahead | null
  error: string | null
  posting: boolean
  posted: QuestionPosted | null
  trailingSlash: boolean
  onTitle: (value: string) => void
  onDetail: (value: string) => void
  onOpener: (title: string) => void
  onAnswered: (item: DiscussionCard) => void
  onRemoveGear: () => void
  onPost: () => void
  onCancel: () => void
  titleRef?: React.Ref<HTMLInputElement>
}

const SlotView: React.VFC<{
  slot: QuestionSlot
  noun: string
  trailingSlash: boolean
  onOpener: (title: string) => void
  onAnswered: (item: DiscussionCard) => void
}> = ({ slot, noun, trailingSlash, onOpener, onAnswered }) => {
  if (slot.mode === 'answered') {
    return (
      <div className={styles.slot}>
        <p className={styles.slotLabel}>{QUESTION_COPY.answeredLabel}</p>
        <ul className={styles.slotList}>
          {slot.items.map(item => (
            <li key={item.id}>
              <Linked
                href={discussionHref(item, trailingSlash)}
                className={styles.slotItem}
                onClick={() => onAnswered(item)}
              >
                {item.title}
              </Linked>
              <span className={styles.slotMeta}>
                {answerCountLine('question', item.answer_count)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )
  }
  if (slot.mode === 'openers') {
    return (
      <div className={styles.slot}>
        <p className={styles.slotNote}>{QUESTION_COPY.openersLead(noun)}</p>
        <ul className={styles.slotList}>
          {slot.openers.map(opener => (
            <li key={opener}>
              <button type="button" className={styles.slotItem} onClick={() => onOpener(opener)}>
                {opener}
              </button>
            </li>
          ))}
        </ul>
      </div>
    )
  }
  return null
}

const ProductSearch: React.VFC<{ typeahead: OwnTypeahead; noun: string }> = ({
  typeahead,
  noun,
}) => {
  const { showDropdown, results, highlight, loading, trimmed } = typeahead
  return (
    <div ref={typeahead.rootRef} className={styles.search}>
      <input
        type="text"
        className={styles.field}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="category-question-products"
        aria-label={QUESTION_COPY.productLabel}
        placeholder={`${QUESTION_COPY.productLabel} · ${QUESTION_COPY.productPlaceholder(noun)}`}
        autoComplete="off"
        value={typeahead.query}
        onFocus={() => typeahead.setOpen(true)}
        onChange={e => {
          typeahead.setQuery(e.target.value)
          typeahead.setOpen(true)
        }}
        onKeyDown={typeahead.onKeyDown}
      />
      {showDropdown && (
        <div className={styles.dropdown} id="category-question-products" role="listbox">
          {results.map((hit, i) => (
            <button
              key={hit.objectID}
              type="button"
              role="option"
              aria-selected={i === highlight}
              className={clsx(styles.hit, { [styles.hitOn]: i === highlight })}
              onMouseEnter={() => typeahead.setHighlight(i)}
              onClick={() => typeahead.select(hit)}
            >
              <span className={styles.hitThumb}>
                {(hit.cloudinaryImageUrl || hit.imageUrl) && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={hit.cloudinaryImageUrl || hit.imageUrl || ''} alt="" loading="lazy" />
                )}
              </span>
              <span className={styles.hitName}>
                {brandedTitle(hit.brand, hit.canonicalName || hit.title)}
              </span>
            </button>
          ))}
          {!results.length && (
            <p className={styles.notice}>
              {loading ? 'Searching…' : `Nothing matches “${trimmed}”.`}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Every composer state from props (the specs render it state by state). */
export const QuestionComposerView: React.VFC<QuestionComposerViewProps> = ({
  noun,
  draft,
  reach,
  slot,
  typeahead,
  error,
  posting,
  posted,
  trailingSlash,
  onTitle,
  onDetail,
  onOpener,
  onAnswered,
  onRemoveGear,
  onPost,
  onCancel,
  titleRef,
}) => {
  const box = styles.composer
  if (posted) {
    return (
      <div className={box} role="status">
        <div className={styles.posted}>
          <p className={styles.postedLine}>{posted.line}</p>
          <TeamAsked line={posted.pill} />
        </div>
        <Linked href={posted.href} className={styles.threadLink}>
          {QUESTION_COPY.seeThread}
        </Linked>
      </div>
    )
  }
  return (
    <form
      className={box}
      aria-label={QUESTION_COPY.formLabel(noun)}
      onSubmit={e => {
        e.preventDefault()
        onPost()
      }}
    >
      {reach && <p className={styles.reach}>{reach}</p>}
      <label className={styles.srOnly} htmlFor="category-question-title">
        {QUESTION_COPY.titleLabel}
      </label>
      <input
        id="category-question-title"
        ref={titleRef}
        type="text"
        className={clsx(styles.field, styles.titleField)}
        placeholder={QUESTION_COPY.titlePlaceholder}
        maxLength={DISCUSSION_LIMITS.titleChars}
        value={draft.title}
        onChange={e => onTitle(e.target.value)}
      />
      <SlotView
        slot={slot}
        noun={noun}
        trailingSlash={trailingSlash}
        onOpener={onOpener}
        onAnswered={onAnswered}
      />
      <label className={styles.srOnly} htmlFor="category-question-detail">
        {QUESTION_COPY.detailLabel}
      </label>
      <textarea
        id="category-question-detail"
        className={clsx(styles.field, styles.detailField)}
        placeholder={QUESTION_COPY.detailPlaceholder}
        maxLength={DISCUSSION_LIMITS.textChars}
        value={draft.detail}
        onChange={e => onDetail(e.target.value)}
      />
      {draft.gear ? (
        <span className={styles.chip}>
          <span className={styles.chipName}>{draft.gear.name}</span>
          <button
            type="button"
            className={styles.chipRemove}
            aria-label={QUESTION_COPY.productRemove(draft.gear.name)}
            onClick={onRemoveGear}
          >
            {'×'}
          </button>
        </span>
      ) : (
        typeahead && <ProductSearch typeahead={typeahead} noun={noun} />
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.foot}>
        <p className={styles.fine}>{QUESTION_COPY.fine}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={onCancel}>
            {QUESTION_COPY.cancel}
          </button>
          <button type="submit" className={styles.primary} disabled={posting}>
            {posting ? QUESTION_COPY.posting : QUESTION_COPY.post}
          </button>
        </div>
      </div>
    </form>
  )
}

/** The posted view from DealHunt's answer: the exact notified count when sent, else the reach. */
export function postedView(
  answer: { discussion?: Partial<DiscussionThread> | null; url?: unknown } | null,
  reach: number | null,
  noun: string,
  trailingSlash: boolean
): QuestionPosted {
  const d = answer?.discussion ?? null
  const pill =
    (d && typeof d.team_mentioned_at === 'string'
      ? teamAskedLine(d as DiscussionCard, Date.now())
      : null) ?? QUESTION_COPY.teamAsked
  return {
    href: postedRedirect(answer?.url, trailingSlash),
    line: postedLine(countOrNull(d?.owners_notified), reach, noun),
    pill,
  }
}

export const QuestionComposer: React.VFC<{
  categoryNodeId: string
  categoryFullSlug: string
  noun: string
  onClose: () => void
}> = ({ categoryNodeId, categoryFullSlug, noun, onClose }) => {
  const trailingSlash = Boolean(publicConfig('useTrailingSlash'))
  const [draft, setDraft] = useState<QuestionDraft>(EMPTY_QUESTION_DRAFT)
  const [restored, setRestored] = useState(false)
  const [reach, setReach] = useState<number | null>(null)
  const [latest, setLatest] = useState<DiscussionCard[] | null>(null)
  const [typed, setTyped] = useState<DiscussionCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [posting, setPosting] = useState(false)
  const [posted, setPosted] = useState<QuestionPosted | null>(null)
  const [wantPost, setWantPost] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)

  const gearProduct = useMemo(
    () => (draft.gear ? { nodeId: draft.gear.node_id, name: draft.gear.name } : null),
    [draft.gear]
  )
  const trackOwner = useOwnerTrack(gearProduct)
  const { showLogIn } = useOwnerSessionOptional()
  const credential = useOwnerCredentialOptional()
  const credentialRef = useRef<OwnerCredential | null>(credential)
  credentialRef.current = credential
  const token = credential?.token ?? null
  const rejectedTokenRef = useRef<string | null>(null)
  const postingRef = useRef(false)

  // The stored draft (this tab, this category), read after mount only; then focus the title.
  useEffect(() => {
    const stored = readQuestionDraft(categoryNodeId)
    if (stored) {
      setDraft(stored)
    }
    setRestored(true)
    titleRef.current?.focus()
  }, [categoryNodeId])
  useEffect(() => {
    if (restored && !posted) {
      writeQuestionDraft(categoryNodeId, draft)
    }
  }, [restored, posted, draft, categoryNodeId])

  // The reach: the category's owners, or the linked product's once a chip is set.
  const productId = draft.gear?.node_id ?? null
  useEffect(() => {
    const ctrl = new AbortController()
    void fetchQuestionReach(categoryNodeId, productId, ctrl.signal).then(n => {
      if (!ctrl.signal.aborted) {
        setReach(n)
      }
    })
    return () => ctrl.abort()
  }, [categoryNodeId, productId])

  // The category's answered questions, once per open.
  useEffect(() => {
    const ctrl = new AbortController()
    void fetchAnsweredQuestions(categoryNodeId, null, ctrl.signal).then(items => {
      if (!ctrl.signal.aborted) {
        setLatest(items)
      }
    })
    return () => ctrl.abort()
  }, [categoryNodeId])

  // From three words: the answered questions that match the title, debounced.
  const query = wordCount(draft.title) >= SLOT_QUERY_WORDS ? draft.title.trim() : null
  useEffect(() => {
    if (!query) {
      setTyped(null)
      return undefined
    }
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      void fetchAnsweredQuestions(categoryNodeId, query, ctrl.signal).then(items => {
        if (!ctrl.signal.aborted) {
          setTyped(items)
        }
      })
    }, SLOT_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [categoryNodeId, query])

  // The product chip: the site search narrowed to product lines under this category.
  const scope = `${categoryFullSlug}/`
  const keep = useCallback(
    (hit: DealHuntSuggestHit) =>
      hitNodeId(hit) !== null && typeof hit.fullSlug === 'string' && hit.fullSlug.startsWith(scope),
    [scope]
  )
  const onPick = useCallback((hit: DealHuntSuggestHit) => {
    const gear = gearFromHit(hit)
    if (gear && gear.kind === 'product') {
      setDraft(d => ({ ...d, gear }))
    }
  }, [])
  const typeahead = useOwnTypeahead(onPick, undefined, keep)

  const post = async (): Promise<void> => {
    if (postingRef.current) {
      return
    }
    postingRef.current = true
    setPosting(true)
    setError(null)
    try {
      const result = await submitQuestion({
        draft,
        categoryNodeId,
        credential: credentialRef.current,
        rejectedToken: rejectedTokenRef.current,
      })
      if (result.kind === 'blocked') {
        setError(result.message)
      } else if (result.kind === 'sign_in') {
        // The draft is already stored (every change writes it); the post runs once a session
        // the proxy has not refused arrives.
        if (result.rejectToken) {
          rejectedTokenRef.current = result.rejectToken
        }
        writeQuestionDraft(categoryNodeId, draft)
        setWantPost(true)
        trackOwner('claim_started', { location: 'question_gate' })
        void showLogIn({ copy: QUESTION_LOGIN_DIALOG })
      } else if (result.kind === 'posted') {
        clearQuestionDraft(categoryNodeId)
        trackOwner('publish_result', { action: 'question', location: 'question' })
        setPosted(postedView(result.answer, reach, noun, trailingSlash))
      } else {
        setError(result.message)
        trackOwner('publish_result', { action: result.code, location: 'question' })
      }
    } finally {
      postingRef.current = false
      setPosting(false)
    }
  }

  // Signed in from the dialog after pressing Post: post now (the writer's rule).
  const postRef = useRef(post)
  postRef.current = post
  useEffect(() => {
    if (wantPost && token && token !== rejectedTokenRef.current) {
      setWantPost(false)
      void postRef.current()
    }
  }, [wantPost, token])

  return (
    <QuestionComposerView
      noun={noun}
      draft={draft}
      reach={reachLine(reach, noun)}
      slot={questionSlot(draft.title, latest, typed, noun)}
      typeahead={typeahead}
      error={error}
      posting={posting}
      posted={posted}
      trailingSlash={trailingSlash}
      titleRef={titleRef}
      onTitle={value => {
        setError(null)
        setDraft(d => ({ ...d, title: value }))
      }}
      onDetail={value => setDraft(d => ({ ...d, detail: value }))}
      onOpener={title => {
        trackOwner('step', { location: 'question_slot' })
        setDraft(d => ({ ...d, title }))
        titleRef.current?.focus()
      }}
      onAnswered={() => trackOwner('step', { location: 'question_slot' })}
      onRemoveGear={() => setDraft(d => ({ ...d, gear: null }))}
      onPost={() => void post()}
      onCancel={onClose}
    />
  )
}

// ── the host ────────────────────────────────────────────────────────────────

export interface CategoryQuestionProps {
  categoryNodeId: string
  /** The category's DealHunt `fullSlug` — the product chip's search scope. */
  categoryFullSlug: string
  noun: string
}

/**
 * The declared strip's ask (slice SG-1): "Something to ask other <noun> owners?" under the
 * buy-again row, then the composer in its place. The CTA is static (`QUESTION_COPY`), so the
 * server HTML stays visitor-invariant; the composer mounts on a click only and reads nothing of
 * the reader before. The undeclared strip carried its own ask button (`QuestionCta` + the band's
 * `QuestionComposer`, one slim strip, 2026-10-17) until 2026-10-27 (Guillaume): a reader who has
 * not named their gear is asked nothing here now.
 * Asking needs a sign-in, told at the ask; the reader writes first and signs in at Post, the
 * draft kept in this tab (`sessionStorage`, per category node), and the post runs once the session
 * arrives — the discussions writer's flow.
 */
const CategoryQuestion: React.VFC<CategoryQuestionProps> = ({
  categoryNodeId,
  categoryFullSlug,
  noun,
}) => {
  const [open, setOpen] = useState(false)
  const trackOwner = useOwnerTrack(null)
  const ctaRef = useRef<HTMLDivElement>(null)
  const seen = useOnScreenOnce(ctaRef, !open)
  const shown = useRef(false)
  useEffect(() => {
    if (seen && !shown.current) {
      shown.current = true
      trackOwner('ask_shown', { location: 'question' })
    }
  }, [seen, trackOwner])

  const onOpen = (): void => {
    trackOwner('band_opened', { location: 'question' })
    setOpen(true)
  }

  if (open) {
    return (
      <QuestionComposer
        categoryNodeId={categoryNodeId}
        categoryFullSlug={categoryFullSlug}
        noun={noun}
        onClose={() => setOpen(false)}
      />
    )
  }
  return (
    <div ref={ctaRef}>
      <QuestionCtaLine noun={noun} onOpen={onOpen} />
    </div>
  )
}

export default CategoryQuestion
